import { useContext, useEffect, useCallback } from 'react';
import classnames from 'clsx';
import generalStyle from '../../../../style.module.css';
import { useDispatch } from 'react-redux';
import { getInventoryAllocationsForProduct } from '../../../../../../store/inventory/inventory.api';
import { deallocateSuccess } from '../../../../../../store/inventory/inventory.actions';
import { FormattedMessage } from 'react-intl';
import { ProductDetailsContext } from '../../../../context';
import { get } from 'lodash-es';
import { AllocationsHeader } from './header';
import { InventoryAllocation } from './inventory-allocation';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { bulkloadFulfillmentPlans } from '../../../../../../store/bulkload/bulkload.actions';
import { Button } from 'ui-components/components/button';
import AsModalWithContext from '../../../../../../components/as-modal/with-context';
import { DeallocationWarningModal } from './deallocation-warning-modal';
import * as InventoryApi from '../../../../../../store/inventory/inventory.api';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { messages } from './messages';
import style from '../style.module.css';

const NoAllocationsMade = () => {
  return (
    <div className={style['no-allocations']}>
      <FormattedMessage {...messages.noAllocations} />
    </div>
  );
};

export const AllocationsGrid = () => {
  const dispatch = useDispatch();
  const { productId } = useContext(ProductDetailsContext);
  const [inventoryAllocations, setInventoryAllocations] = useAsyncSafeState([]);
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [isWarningModalOpen, setIsWarningModalOpen] = useAsyncSafeState(false);
  const onSetWarningOpen = useCallback(() => {
    setIsWarningModalOpen(!isWarningModalOpen);
  }, [isWarningModalOpen, setIsWarningModalOpen]);
  const [shouldLoadAllocations, setShouldLoadAllocations] = useAsyncSafeState(true);
  const onConfirmDeallocation = useCallback(() => {
    const fulfillmentPlanIds = inventoryAllocations.map((alloc) => alloc.fulfillmentPlanId);
    InventoryApi.deallocateFulfillmentPlansPromise(fulfillmentPlanIds).then((data) => {
      const didSucceed = data.status === 'DeallocationSuccessful';
      if (!didSucceed) {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.deallocationFailureTitle} />,
          content: <FormattedMessage {...messages.deallocationFailureMessage} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
      } else {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.deallocationSuccessTitle} />,
          content: <FormattedMessage {...messages.deallocationSuccessMessage} />,
          notificationType: NOTIFICATION_TYPE_SUCCESS,
        });
      }
      setShouldLoadAllocations(true);
      dispatch(deallocateSuccess());
    });
    setIsWarningModalOpen(false);
  }, [dispatch, setIsWarningModalOpen, setShouldLoadAllocations, inventoryAllocations]);

  useEffect(() => {
    if (!productId) {
      setIsLoading(false);
    }
    const allocationsPromise = productId && shouldLoadAllocations ? getInventoryAllocationsForProduct(productId) : undefined;
    if (allocationsPromise) {
      allocationsPromise.promise.then((response) => {
        const allocations = get(response, 'data.allocations', []);
        const fulfillmentPlanIds = allocations.map((alloc) => alloc.fulfillmentPlanId);
        dispatch(bulkloadFulfillmentPlans(fulfillmentPlanIds));
        setInventoryAllocations(allocations);
        setIsLoading(false);
        setShouldLoadAllocations(false);
      });
    }
    return () => allocationsPromise?.cancel();
  }, [productId, setInventoryAllocations, setIsLoading, dispatch, shouldLoadAllocations, setShouldLoadAllocations]);

  return (
    <>
      <div className={style['locations-container-flex']}>
        <div className={classnames(generalStyle['shipping-defaults-header-text'], style['allocations-title'])}>
          <FormattedMessage {...messages.allocatedOrdersHeader} />
        </div>
        <Button
          className={style['deallocation-button']}
          disabled={inventoryAllocations.length === 0}
          onClick={onSetWarningOpen}
          theme='secondary'
        >
          <FormattedMessage {...messages.allocationsDeallocationLink} onCloseModal={onSetWarningOpen} />
        </Button>
      </div>
      <div className={style['allocations-container']}>
        <AllocationsHeader />
        {inventoryAllocations.map((inv) => {
          return (
            <InventoryAllocation
              key={inv.orderNumber}
              fulfillmentPlanId={inv.fulfillmentPlanId}
              numberAllocated={inv.quantity}
              orderNumber={inv.orderNumber}
              orderStatus={inv.orderGridStatus}
              toteId={inv.toteId}
            />
          );
        })}
        {inventoryAllocations.length === 0 && !isLoading && <NoAllocationsMade />}
        {isLoading && <LoadingIndicator className={style['loading-indicator']} theme='grey' />}
        {isWarningModalOpen && (
          <AsModalWithContext Context={ProductDetailsContext}>
            <DeallocationWarningModal
              onCloseModal={onSetWarningOpen}
              onConfirmation={onConfirmDeallocation}
              orderCount={inventoryAllocations.length}
            />
          </AsModalWithContext>
        )}
      </div>
    </>
  );
};
